"use client";

import { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Check, ArrowRight, ShieldCheck, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
gsap.registerPlugin(useGSAP, ScrollTrigger);
interface TierItem {
  id: string;
  name: string;
  tagline: string;
  price: string;
  cadence: string;
  description: string;
  popular: boolean;
  badge: string;
  features: string[];
}
const pricingData: TierItem[] = [{
  id: "foundation",
  name: "Foundation Monthly SEO",
  tagline: "For small businesses starting their search journey",
  price: "£850",
  cadence: "per month",
  description: "Core technical health and targeted keyword optimization managed directly by me.",
  popular: false,
  badge: "Core SEO",
  features: ["Initial technical & site architecture audit", "Monthly hands-on technical fixes & monitoring", "Targeted keyword & content optimization", "Google Search Console & Analytics health check", "Monthly performance report with direct loom summary", "Direct email access with 24-hour turnaround"]
}, {
  id: "growth",
  name: "Growth Monthly SEO",
  tagline: "For scaling businesses targeting national search visibility",
  price: "£1,650",
  cadence: "per month",
  description: "My most popular tier: complete end-to-end strategy, content, and organic expansion.",
  popular: true,
  badge: "Most Popular",
  features: ["Everything included in Foundation tier", "Comprehensive monthly technical sprint delivery", "On-page content optimization & new topic creation", "Ethical, context-relevant link acquisition strategy", "AI search visibility & structured data enhancements", "Fortnightly video check-in & progress reporting", "Direct WhatsApp and email communication"]
}, {
  id: "enterprise",
  name: "Enterprise SEO Management",
  tagline: "For complex websites and multi-market search programs",
  price: "Bespoke",
  cadence: "tailored scope",
  description: "Dedicated consultancy embedded with your team for international, ecommerce, or large catalogs.",
  popular: false,
  badge: "Full Partnership",
  features: ["Custom multi-market & international SEO architecture", "Continuous server log analysis & real-time monitoring", "Deep technical migration & redesign support", "Full digital PR and link strategy integration", "Custom Looker Studio dashboards & KPI tracking", "Direct Slack channel access with priority response", "Weekly strategy calls with your leadership"]
}];
export default function PricingTiers() {
  const container = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = container.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".pricing-header", {
        y: 24
      });
      gsap.to(".pricing-header", {
        opacity: 1,
        y: 0,
        duration: 0.6,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 85%",
          once: true
        }
      });
      gsap.set(".pricing-card", {
        y: 36
      });
      gsap.to(".pricing-card", {
        opacity: 1,
        y: 0,
        duration: 0.65,
        stagger: 0.15,
        ease: "power2.out",
        scrollTrigger: {
          trigger: ".pricing-grid",
          start: "top 85%",
          once: true
        }
      });
      gsap.set(".pricing-footer", {
        y: 20
      });
      gsap.to(".pricing-footer", {
        opacity: 1,
        y: 0,
        duration: 0.5,
        ease: "power2.out",
        scrollTrigger: {
          trigger: ".pricing-footer",
          start: "top 90%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([".pricing-header", ".pricing-card", ".pricing-footer"], {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: container
  });
  return <section id="monthlypricingtiers" data-nav="light" className="bg-background text-foreground py-20 md:py-28">
      <div ref={container} className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="pricing-header mx-auto max-w-3xl text-center mb-16" style={{
        opacity: 0
      }}>
          <Badge variant="secondary" className="mb-4 text-xs font-semibold uppercase tracking-wider">
            Clear Consulting Retainers
          </Badge>
          <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-4">
            Transparent monthly SEO retainers.
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            Every retainer operates on a rolling month-to-month basis with clear sprint deliverables, prioritized technical implementation, and defined monthly milestones. Flexible 30-day terms with no extended lock-in contracts—just consistent monthly momentum.
          </p>
        </div>

        {/* Pricing Cards Grid */}
        <div className="pricing-grid grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch mb-16">
          {pricingData.map((tier, index) => {
          const isPopular = tier.popular;
          return <div key={tier.id} data-index={index} className="pricing-card flex h-full" style={{
            opacity: 0
          }}>
                <Card className={`w-full flex flex-col justify-between transition-all duration-300 rounded-xl relative ${isPopular ? "border-2 border-primary shadow-xl bg-card" : "border border-border shadow-sm hover:shadow-md bg-card"}`}>
                  {isPopular && <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-primary text-primary-foreground shadow-sm px-3 py-1 flex items-center gap-1.5 text-xs font-semibold">
                        <Sparkles className="size-3.5" />
                        {tier.badge}
                      </Badge>
                    </div>}

                  <div>
                    <CardHeader className={isPopular ? "pt-8 pb-4" : "pt-6 pb-4"}>
                      {!isPopular && <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1 block">
                          {tier.badge}
                        </span>}
                      <CardTitle className="text-xl sm:text-2xl font-bold text-foreground">
                        {tier.name}
                      </CardTitle>
                      <CardDescription className="text-xs text-muted-foreground mt-1">
                        {tier.tagline}
                      </CardDescription>
                      
                      <div className="mt-6 mb-2">
                        <div className="flex items-baseline gap-1.5">
                          <span className="text-3xl sm:text-4xl font-extrabold text-foreground tracking-tight">
                            {tier.price}
                          </span>
                          <span className="text-sm font-medium text-muted-foreground">
                            /{tier.cadence}
                          </span>
                        </div>
                      </div>

                      <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                        {tier.description}
                      </p>
                    </CardHeader>

                    <CardContent className="pt-2 pb-6">
                      <Separator className="mb-6" />
                      <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-4">
                        What I include in this tier:
                      </p>
                      <ul className="space-y-3">
                        {tier.features.map((feature, fIndex) => <li key={fIndex} data-index={fIndex} className="flex items-start gap-2.5 text-xs sm:text-sm text-foreground/90 leading-normal">
                            <span className="mt-0.5 shrink-0 rounded-full p-0.5 bg-primary/10 text-primary">
                              <Check className="size-3.5 stroke-[2.5]" />
                            </span>
                            <span>{feature}</span>
                          </li>)}
                      </ul>
                    </CardContent>
                  </div>

                  <CardFooter className="pt-2 pb-6">
                    <Button asChild size="lg" className={`w-full font-medium transition-all ${isPopular ? "bg-accent text-accent-foreground hover:bg-accent/90 shadow-md" : "bg-primary text-primary-foreground hover:bg-primary/90 shadow-xs"}`}>
                      <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                        <span>Book a Free SEO Consultation</span>
                        <ArrowRight className="size-4" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </div>;
        })}
        </div>

        {/* Honest Pricing Note & Trust Markers */}
        <div className="pricing-footer bg-muted/60 border border-border rounded-xl p-6 sm:p-8 max-w-4xl mx-auto text-center" style={{
        opacity: 0
      }}>
          <div className="flex items-center justify-center gap-2 text-primary font-semibold text-sm mb-2">
            <ShieldCheck className="size-5" />
            <span>My Honest SEO Pricing Promise</span>
          </div>
          <p className="text-foreground font-medium text-sm sm:text-base mb-1">
            No fixed ranking guarantees. Transparent scope. No long-term lock-in contracts.
          </p>
          <p className="text-muted-foreground text-xs sm:text-sm max-w-2xl mx-auto leading-relaxed">
            I believe in earning your trust every month. You receive itemized deliverable tracking, complete ownership of all created assets, and direct consultation whenever you need it.
          </p>
        </div>
      </div>
    </section>;
}